import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiLoader, FiLink, FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { cCreateGroup, cDiscoverGroups, cJoinGroup, cMyGroups, cUpload, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import GroupCard from '../../components/community/GroupCard';
import { BLUE, BLUE_GRADIENT } from '../../components/community/brand';

/**
 * Groups — my groups + the public directory + “create group”.
 *
 * A group is **public** (everybody finds it and joins) or **private** (only the
 * invite link `/join/<code>` works); the logo, nickname, members and admins are
 * managed on the group page.
 */
const EMPTY = { name: '', about: '', avatar: '', cover: '', is_public: false, allow_member_invite: true };

export default function GroupsPage() {
  const { t } = useLanguage();
  const { user, requireAuth, openAuth } = useCommunity();
  const navigate = useNavigate();
  const [tab, setTab] = useState('mine');       // mine | discover
  const [mine, setMine] = useState({ items: [], can_create: false, limit: 20 });
  const [found, setFound] = useState([]);
  const [q, setQ] = useState('');
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  const loadMine = () => (user
    ? cMyGroups().then(setMine).catch(() => {})
    : Promise.resolve(setMine({ items: [], can_create: false, limit: 20 })));
  const loadDiscover = (term = '') => cDiscoverGroups({ q: term || undefined, limit: 30 })
    .then((r) => setFound(r.items || []))
    .catch(() => setFound([]));

  useEffect(() => {
    loadMine();
    loadDiscover('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const res = await cUpload([file]);
      setForm((f) => ({ ...f, avatar: res.urls?.[0] || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setLogoBusy(false);
    }
  };

  /** រូបភាពគម្របក្រុម — the banner of the new group. */
  const pickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const res = await cUpload([file]);
      setForm((f) => ({ ...f, cover: res.urls?.[0] || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setCoverBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    if (!form.name.trim()) { toast.error(t('groupName')); return; }
    setBusy(true);
    try {
      const res = await cCreateGroup({ ...form, name: form.name.trim(), about: form.about.trim() });
      toast.success(`${t('groupCreate')} ✓`);
      setForm(EMPTY);
      setCreating(false);
      navigate(`/groups/${res.group.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const join = async (group) => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    try {
      await cJoinGroup(group.id);
      toast.success(t('groupJoined'));
      navigate(`/groups/${group.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const input = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm outline-none focus:border-gray-400';

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold ${tab === key ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
      style={tab === key ? { background: BLUE } : undefined}
    >
      {label}
    </button>
  );

  const items = tab === 'mine' ? mine.items : found;

  return (
    <div className="mx-auto max-w-[1200px] px-3 lg:px-4 py-4">
      <header className="flex items-center gap-3 flex-wrap mb-3">
        <h1 className="text-lg sm:text-xl font-extrabold">{t('groupsTitle')}</h1>
        <div className="ml-auto flex items-center gap-2">
          {tab === 'discover' && (
            <div className="relative">
              <FiSearch className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); loadDiscover(e.target.value); }}
                placeholder={t('groupDiscover')}
                className="pl-9 pr-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 text-sm w-40 sm:w-56"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => (user ? setCreating((v) => !v) : openAuth('signin', t('loginRequiredDesc')))}
            disabled={!!user && !mine.can_create}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white text-xs sm:text-sm font-bold disabled:opacity-50"
            style={{ background: BLUE }}
          >
            <FiPlus className="w-4 h-4" /> {t('groupCreate')}
          </button>
        </div>
      </header>

      {/* join with an invite link (the private groups) */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const raw = code.trim();
          if (!raw) return;
          if (!user) { openAuth('signin', t('loginRequiredDesc')); return; }
          const m = raw.match(/([A-Za-z0-9_-]{4,})\/?$/);
          navigate(`/join/${m ? m[1] : raw}`);
        }}
        className="mb-3 flex items-center gap-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-2.5"
      >
        <FiLink className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('groupJoinByLink')}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none"
        />
        <button type="submit" className="px-3.5 py-2 rounded-full text-xs font-bold text-white" style={{ background: BLUE }}>
          {t('groupJoin')}
        </button>
      </form>

      {creating && (
        <form onSubmit={create} className="mb-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 space-y-2.5 anim-pop">
          <div className="flex items-center justify-between">
            <h2 className="font-extrabold text-sm">{t('groupCreate')}</h2>
            <button type="button" onClick={() => setCreating(false)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t('close')}>
              <FiX className="w-4 h-4" />
            </button>
          </div>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('groupName')} className={input} />
          <textarea value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={2} placeholder={t('groupAbout')} className={input} />
          <div>
            <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('groupCover')}</span>
            {form.cover ? (
              <img src={fullUrl(form.cover)} alt={t('groupCover')} className="w-full h-24 rounded-xl object-cover mb-1.5" />
            ) : (
              <span className="block w-full h-24 rounded-xl mb-1.5" style={{ background: BLUE_GRADIENT }} />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                <input type="file" accept="image/*" hidden onChange={pickCover} />
                {coverBusy && <FiLoader className="w-4 h-4 animate-spin" />}
                {t('groupUploadCover')}
              </label>
              {form.cover && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, cover: '' }))} className="text-xs font-bold text-red-500 hover:underline">
                  {t('remove')}
                </button>
              )}
              <span className="text-[11px] text-gray-400">{t('groupCoverHint')}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
              <input type="file" accept="image/*" hidden onChange={pickLogo} />
              {logoBusy && <FiLoader className="w-4 h-4 animate-spin" />}
              {form.avatar ? `${t('groupLogo')} ✓` : t('groupUploadLogo')}
            </label>
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))} />
              {t('groupPublic')}
            </label>
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={form.allow_member_invite} onChange={(e) => setForm((f) => ({ ...f, allow_member_invite: e.target.checked }))} />
              {t('groupAllowMemberInvite')}
            </label>
          </div>
          <p className="text-[11px] text-gray-400">
            {form.is_public ? t('groupPublicHint') : t('groupPrivateHint')}
          </p>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold disabled:opacity-60" style={{ background: BLUE }}>
            {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />} {t('groupCreate')}
          </button>
        </form>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {tabBtn('mine', t('groupMyGroups'))}
        {tabBtn('discover', t('groupDiscover'))}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-400">{t('groupEmpty')}</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((g) => <GroupCard key={g.id} group={g} onJoin={join} />)}
        </div>
      )}
    </div>
  );
}
